<template>
  <table class="min-w-full">
    <thead class="bg-gray-50">
      <tr class="first-child:pl-3 last-child:pr-3 child:py-2 sticky top-0 text-xs child:border-b child:border-gray-200 font-semibold text-gray-600">
        <th class="text-left uppercase tracking-wider bg-gray-50">
          Contract Works
        </th>

        <th class="text-right uppercase tracking-wider bg-gray-50 pr-3">
          Subcontract Sum
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          Current Claim
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          Current Certified
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          Variance
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          Previous
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50 pr-3">
          Cumulative
        </th>

        <th class="text-right uppercase tracking-wider bg-blue-50">
          <div class="flex items-center justify-end space-x-2">
            <button
              type="button"
              class="btn-icon btn-icon-default"
              :title="showPercentages ? 'Hide Percentages' : 'Show Percentages'"
              @click.stop="showPercentages = !showPercentages"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"
                :class="[{'rotate-180': !showPercentages}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
            </button>

            <span v-if="showPercentages">Current Claim %</span>
          </div>
        </th>

        <th
          v-if="showPercentages"
          class="text-right uppercase tracking-wider bg-blue-50"
        >
          Current Certified %
        </th>

        <th
          v-if="showPercentages"
          class="text-right uppercase tracking-wider bg-blue-50"
        >
          Previous %
        </th>

        <th
          v-if="showPercentages"
          class="text-right uppercase tracking-wider bg-blue-50 pr-3"
        >
          Cumulative %
        </th>

        <th class="text-right uppercase tracking-wider bg-gray-50">
          Cost To Complete
        </th>
      </tr>
    </thead>

    <tbody
      v-if="claim"
      class="bg-white divide-y divide-gray-200"
    >
      <LumpSumTradeRow
        v-for="(trade, i) in trades"
        :key="i"
        :can-edit-claim="canEditClaim"
        :package-trade-items="trade.packageTradeItems"
        :trade="trade.trade"
        :claim="claim"
        :show-percentages="showPercentages"
      />

      <!-- Subtotal Row -->
      <tr class="first-child:pl-3 last-child:pr-3 child:py-4 bg-gray-50 border-t-2 border-gray-200">
        <td class="font-semibold text-gray-900">
          Subtotal
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ australianCurrency(totalCosts.trades.budget) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.trades.current_claim) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.trades.current_certified) }}
        </td>

        <td
          class="text-right font-semibold"
          :class="{ 'text-red-600': totalCosts.trades.variance !== 0, 'text-gray-900': totalCosts.trades.variance === 0 }"
        >
          {{ australianCurrency(totalCosts.trades.variance) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.trades.previous) }}
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ australianCurrency(totalCosts.trades.cumulativeCertified) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          <span v-if="showPercentages">
            {{ isNumber(totalCosts.trades.current_claim / totalCosts.trades.budget) ? australianNumber((totalCosts.trades.current_claim / totalCosts.trades.budget) * 100) : 0 }}%
          </span>
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ isNumber(totalCosts.trades.current_certified / totalCosts.trades.budget) ? australianNumber((totalCosts.trades.current_certified / totalCosts.trades.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ isNumber(totalCosts.trades.previous / totalCosts.trades.budget) ? australianNumber((totalCosts.trades.previous / totalCosts.trades.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900 pr-3"
        >
          {{ isNumber(totalCosts.trades.cumulativeCertified / totalCosts.trades.budget) ? australianNumber((totalCosts.trades.cumulativeCertified / totalCosts.trades.budget) * 100) : 0 }}%
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(Math.abs(totalCosts.trades.budget - totalCosts.trades.cumulativeCertified)) }}
        </td>
      </tr>

      <template
        v-for="(orders, key) in variationOrdersByType"
        :key="key"
      >
        <VariationOrderRow
          :claim="claim"
          :variation-orders="orders"
          :title="VariationOrderTitles[key]"
          :variation-order-type="VariationOrderType[key]"
          :show-percentages="showPercentages"
        />
      </template>

      <!-- Variation Orders Subtotal Row -->
      <tr
        v-if="Object.keys(variationOrders).length > 0"
        class="first-child:pl-3 last-child:pr-3 child:py-4 bg-gray-50 border-t-2 border-gray-200"
      >
        <td class="font-semibold text-gray-900">
          Subtotal
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ australianCurrency(totalCosts.variationOrders.budget) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.variationOrders.current_claim) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.variationOrders.current_certified) }}
        </td>

        <td
          class="text-right font-semibold"
          :class="{ 'text-red-600': totalCosts.variationOrders.variance !== 0, 'text-gray-900': totalCosts.variationOrders.variance === 0 }"
        >
          {{ australianCurrency(totalCosts.variationOrders.variance) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.variationOrders.previous) }}
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ australianCurrency(totalCosts.variationOrders.cumulativeCertified) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          <span v-if="showPercentages">
            {{ totalCosts.variationOrders.budget > 0 ? australianNumber((totalCosts.variationOrders.current_claim / totalCosts.variationOrders.budget) * 100) : 0 }}%
          </span>
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ totalCosts.variationOrders.budget > 0 ? australianNumber((totalCosts.variationOrders.current_certified / totalCosts.variationOrders.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ totalCosts.variationOrders.budget > 0 ? australianNumber((totalCosts.variationOrders.previous / totalCosts.variationOrders.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900 pr-3"
        >
          {{ totalCosts.variationOrders.budget > 0 ? australianNumber((totalCosts.variationOrders.cumulativeCertified / totalCosts.variationOrders.budget) * 100) : 0 }}%
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.variationOrders.budget - totalCosts.variationOrders.cumulativeCertified) }}
        </td>
      </tr>

      <DayworksSection
        v-if="claim && hasDayworks"
        :claim="claim"
        :show-percentages="showPercentages"
      />

      <!-- Dayworks Subtotal Row -->
      <tr
        v-if="hasDayworks"
        class="first-child:pl-3 last-child:pr-3 child:py-4 bg-gray-50 border-t-2 border-gray-200"
      >
        <td class="font-semibold text-gray-900">
          Dayworks Subtotal
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ australianCurrency(totalCosts.dayworks.calculated) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.dayworks.current_claim) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.dayworks.current_certified) }}
        </td>

        <td
          class="text-right font-semibold"
          :class="{ 'text-red-600': totalCosts.dayworks.variance !== 0, 'text-gray-900': totalCosts.dayworks.variance === 0 }"
        >
          {{ australianCurrency(totalCosts.dayworks.variance) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.dayworks.previous) }}
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ australianCurrency(totalCosts.dayworks.cumulative) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          <span v-if="showPercentages">
            {{ totalCosts.dayworks.calculated > 0 ? australianNumber((totalCosts.dayworks.current_claim / totalCosts.dayworks.calculated) * 100) : 0 }}%
          </span>
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ totalCosts.dayworks.calculated > 0 ? australianNumber((totalCosts.dayworks.current_certified / totalCosts.dayworks.calculated) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ totalCosts.dayworks.calculated > 0 ? australianNumber((totalCosts.dayworks.previous / totalCosts.dayworks.calculated) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900 pr-3"
        >
          {{ totalCosts.dayworks.calculated > 0 ? australianNumber((totalCosts.dayworks.cumulative / totalCosts.dayworks.calculated) * 100) : 0 }}%
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.dayworks.calculated - totalCosts.dayworks.cumulative) }}
        </td>
      </tr>

      <!-- Grand Total Row -->
      <tr class="first-child:pl-3 last-child:pr-3 child:py-4 bg-blue-50 border-t-2 border-blue-200 sticky bottom-0">
        <td class="font-bold text-blue-900">
          Grand Total
        </td>

        <td class="text-right font-bold text-blue-900 pr-3">
          {{ australianCurrency(totalCosts.variationOrders.budget + totalCosts.trades.budget + totalCosts.dayworks.calculated) }}
        </td>

        <td class="text-right font-bold text-blue-900">
          {{ australianCurrency(totalCosts.variationOrders.current_claim + totalCosts.trades.current_claim + totalCosts.dayworks.current_claim) }}
        </td>

        <td class="text-right font-bold text-blue-900">
          {{ australianCurrency(totalCosts.variationOrders.current_certified + totalCosts.trades.current_certified + totalCosts.dayworks.current_certified) }}
        </td>

        <td
          class="text-right font-bold"
          :class="{ 'text-red-600': totalCosts.variationOrders.variance + totalCosts.trades.variance + totalCosts.dayworks.variance !== 0, 'text-blue-900': totalCosts.variationOrders.variance + totalCosts.trades.variance + totalCosts.dayworks.variance === 0 }"
        >
          {{ australianCurrency(totalCosts.variationOrders.variance + totalCosts.trades.variance + totalCosts.dayworks.variance) }}
        </td>

        <td class="text-right font-bold text-blue-900">
          {{ australianCurrency(totalCosts.variationOrders.previous + totalCosts.trades.previous + totalCosts.dayworks.previous) }}
        </td>

        <td class="text-right font-bold text-blue-900 pr-3">
          {{ australianCurrency(totalCosts.variationOrders.cumulativeCertified + totalCosts.trades.cumulativeCertified + totalCosts.dayworks.cumulative) }}
        </td>

        <td class="text-right font-bold text-blue-900">
          <span v-if="showPercentages">
            {{ totalCosts.variationOrders.budget + totalCosts.trades.budget + totalCosts.dayworks.calculated > 0 ? australianNumber(((totalCosts.variationOrders.current_claim + totalCosts.trades.current_claim + totalCosts.dayworks.current_claim) / (totalCosts.variationOrders.budget + totalCosts.trades.budget + totalCosts.dayworks.calculated)) * 100) : 0 }}%
          </span>
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-bold text-blue-900"
        >
          {{ totalCosts.variationOrders.budget + totalCosts.trades.budget + totalCosts.dayworks.calculated > 0 ? australianNumber(((totalCosts.variationOrders.current_certified + totalCosts.trades.current_certified + totalCosts.dayworks.current_certified) / (totalCosts.variationOrders.budget + totalCosts.trades.budget + totalCosts.dayworks.calculated)) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-bold text-blue-900"
        >
          {{ totalCosts.variationOrders.budget + totalCosts.trades.budget + totalCosts.dayworks.calculated > 0 ? australianNumber(((totalCosts.variationOrders.previous + totalCosts.trades.previous + totalCosts.dayworks.previous) / (totalCosts.variationOrders.budget + totalCosts.trades.budget + totalCosts.dayworks.calculated)) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-bold text-blue-900 pr-3"
        >
          {{ totalCosts.variationOrders.budget + totalCosts.trades.budget + totalCosts.dayworks.calculated > 0 ? australianNumber(((totalCosts.variationOrders.cumulativeCertified + totalCosts.trades.cumulativeCertified + totalCosts.dayworks.cumulative) / (totalCosts.variationOrders.budget + totalCosts.trades.budget + totalCosts.dayworks.calculated)) * 100) : 0 }}%
        </td>

        <td class="text-right font-bold text-blue-900">
          {{ australianCurrency(totalCosts.variationOrders.budget - totalCosts.variationOrders.cumulativeCertified + totalCosts.trades.budget - totalCosts.trades.cumulativeCertified + totalCosts.dayworks.calculated - totalCosts.dayworks.cumulative) }}
        </td>
      </tr>
    </tbody>
  </table>

  <!-- Footnote -->
  <div
    v-if="Object.keys(variationOrders).length > 0"
    class="p-4 bg-yellow-100 border-t border-yellow-200"
  >
    <p class="text-sm text-yellow-800">
      <strong>Note:</strong> In order to claim for a variation, the variation must be in the system.
    </p>
  </div>
</template>

<script setup lang="ts">
import { useSignedData } from '@/composables/useSignedData';
import { australianCurrency, australianNumber, isNumber } from '@/helpers';
import Claim, { ClaimStatus } from '@/models/Claim';
import Labour from '@/models/Labour';
import PackageTradeItem from '@/models/PackageTradeItem';
import Plant from '@/models/Plant';
import Trade from '@/models/Trade';
import TradeItem from '@/models/TradeItem';
import VariationOrder, { VariationOrderTitles, VariationOrderType } from '@/models/VariationOrder';
import { useClaimsStore } from '@/store/claims';
import { useDayworksCostsStore } from '@/store/dayworksCosts';
import { usePackageTradeItemsStore } from '@/store/packageTradeItems';
import { useVariationOrderCostsStore } from '@/store/variationOrderCosts';
import { useVariationOrdersStore } from '@/store/variationOrders';
import { Collection, useRepo } from 'pinia-orm';
import { computed, ref } from 'vue';
import DayworksSection from './DayworksSection.vue';
import LumpSumTradeRow from './LumpSumTradeRow.vue';
import VariationOrderRow from './VariationOrderRow.vue';

const props = defineProps<{
  claimId: string;
}>();

const showPercentages = ref(false);
const signedParams = useSignedData();
const claimsStore = useClaimsStore();
const packageTradeItemsStore = usePackageTradeItemsStore();
const variationOrdersStore = useVariationOrdersStore();
const variationOrderCostsStore = useVariationOrderCostsStore();
const dayworksCostsStore = useDayworksCostsStore();

const claim = computed(() => {
  return claimsStore.models
    .with('trades', (query) => {
      query.with('trade_items');
    })
    .find(parseInt(props.claimId));
});

const tradeItemIds = computed(() => {
  return (signedParams.models.tradeItems as TradeItem[]).map((tradeItem) => {
    return tradeItem.id;
  });
});

const packageTradeItems = computed(() => {
  return packageTradeItemsStore.models
    .whereIn('trade_item_id', tradeItemIds.value)
    .with('trade_item', (query) => query.with('trade'))
    .with('lump_sum_costs', (query) => query.where('claim_id', parseInt(props.claimId)))
    .orderBy('sort_order', 'asc')
    .with('wbs_code')
    .get();
});

const trades = computed(() => {
  const trades = new Map<Id, { trade?: Trade; packageTradeItems: PackageTradeItem[] }>();

  trades.set('Package', {
    trade: undefined,
    packageTradeItems: [],
  });

  packageTradeItems.value.forEach((packageTradeItem) => {
    if(!packageTradeItem.trade_item) {
      return;
    }

    const tradeKey = packageTradeItem.trade_item.trade?.id ?? 'Package';
    const trade = trades.get(tradeKey);

    if(trade) {
      trade.packageTradeItems.push(packageTradeItem);
    } else {
      trades.set(tradeKey, {
        trade: packageTradeItem.trade_item.trade,
        packageTradeItems: [packageTradeItem],
      });
    }
  });

  if(trades.get('Package').packageTradeItems.length === 0) {
    trades.delete('Package');
  }

  return Array.from(trades.values());
});

const variationOrderIds = computed(() => {
  return (signedParams.models.variation_orders as VariationOrder[]).map((variationOrder) => {
    return variationOrder.id;
  });
});

const variationOrders = computed(() => {
  return variationOrdersStore.models
    .whereIn('id', variationOrderIds.value)
    // .where(
    //   'type',
    //   VariationOrderType[key],
    // )
    .with('trade_items')
    .with('costs', (query) => query.where('claim_id', parseInt(props.claimId)))
    .get();
});

const variationOrdersByType = computed(() => {
  if(!signedParams.models.variation_orders) {
    return {};
  }

  const costs: Partial<{ [k in keyof typeof VariationOrderType]: Collection<VariationOrder> }> = {};

  for(const key in VariationOrderType) {
    costs[key] = variationOrdersStore.models
      .whereIn('id', variationOrderIds.value)
      .where(
        'type',
        VariationOrderType[key],
      )
      .with('trade_items')
      .with('costs', (query) => query.where('claim_id', parseInt(props.claimId)))
      .orderBy((variationOrder: VariationOrder) => variationOrder.client_ref || variationOrder.code)
      .get();
  }

  return costs;
});

const dayworksLabours = computed(() => {
  return useRepo(Labour).where('$claim_id', parseInt(props.claimId)).get();
});

const dayworksPlants = computed(() => {
  return useRepo(Plant).where('$claim_id', parseInt(props.claimId)).get();
});

const dayworksCosts = computed(() => {
  return dayworksCostsStore.models
    .where('claim_id', parseInt(props.claimId))
    .get();
});

const hasDayworks = computed(() => {
  return dayworksLabours.value.length > 0 || dayworksPlants.value.length > 0 || dayworksCosts.value.length > 0;
});

const totalCosts = computed(() => {
  const totals = {
    trades: {
      budget: 0,
      current_claim: 0,
      current_certified: 0,
      cumulativeCertified: 0,
      previous: 0,
      variance: 0,
    },

    variationOrders: {
      budget: 0,
      current_claim: 0,
      current_certified: 0,
      cumulativeCertified: 0,
      previous: 0,
      variance: 0,
    },

    dayworks: {
      calculated: 0,
      current_claim: 0,
      current_certified: 0,
      cumulative: 0,
      previous: 0,
      variance: 0,
    },
  };

  if(claim.value) {
    packageTradeItems.value.forEach((packageTradeItem) => {
      totals.trades.budget += packageTradeItem.value;
      totals.trades.previous += packageTradeItem.previous(claim.value);

      packageTradeItem.lump_sum_costs.forEach((cost) => {
        totals.trades.current_claim += cost.current_claim || 0;
        totals.trades.current_certified += cost.current_certified || 0;
      });
    });

    // Calculate variation order totals
    variationOrders.value.forEach((variationOrder) => {
      totals.variationOrders.budget += variationOrder.totalBudget;
      totals.variationOrders.previous += variationOrder.previous(claim.value);

      const variationOrderCost = variationOrder.costs[0];

      if(variationOrderCost) {
        totals.variationOrders.current_claim += variationOrderCost.current_claim || 0;
        totals.variationOrders.current_certified += variationOrderCost.current_certified || 0;
      }
    });

    dayworksLabours.value.forEach((labour) => {
      const cost = dayworksCosts.value.find((dayworksCost) => dayworksCost.labour_id === labour.id);

      totals.dayworks.calculated += labour.calculatedAmount || 0;
      totals.dayworks.current_claim += cost?.current_claim || 0;
      totals.dayworks.current_certified += cost?.current_certified || 0;
      totals.dayworks.previous += labour.$previous || 0;
      totals.dayworks.variance += (cost?.current_claim || 0) - (cost?.current_certified || 0);
    });

    dayworksPlants.value.forEach((plant) => {
      const cost = dayworksCosts.value.find((dayworksCost) => dayworksCost.plant_id === plant.id);

      totals.dayworks.calculated += plant.calculatedAmount || 0;
      totals.dayworks.current_claim += cost?.current_claim || 0;
      totals.dayworks.current_certified += cost?.current_certified || 0;
      totals.dayworks.previous += plant.$previous || 0;
      totals.dayworks.variance += (cost?.current_claim || 0) - (cost?.current_certified || 0);
    });
  }

  totals.trades.cumulativeCertified = totals.trades.current_certified + totals.trades.previous;
  totals.trades.variance = totals.trades.current_claim - totals.trades.current_certified;

  totals.variationOrders.cumulativeCertified = totals.variationOrders.current_certified +
    totals.variationOrders.previous;

  totals.variationOrders.variance = totals.variationOrders.current_claim - totals.variationOrders.current_certified;

  totals.dayworks.cumulative = totals.dayworks.previous + totals.dayworks.current_claim;

  return totals;
});

const canEditClaim = computed(() => {
  return signedParams.meta.edit_claim &&
    (claim.value?.status === ClaimStatus.draft_subcontractor) && !signedParams.meta.claim_overdue;
});
</script>

<style scoped></style>
